import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Seo from "@/components/base/Seo";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import BrandLogo from "@/components/feature/BrandLogo";
import { IS_MOCK } from "@/services/config";
import { DEMO_ACCOUNT } from "@/services/ownerApi";

export default function Login() {
  const { login, user, loading } = useAuth();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/dashboard";

  // Already signed in when landing here (e.g. a stored session)? Send them
  // to the right portal. Skipped once this page starts its own sign-in flow.
  const arrivedSignedIn = useRef(false);
  useEffect(() => {
    if (loading || arrivedSignedIn.current) return;
    if (user?.role === "owner") navigate("/dashboard", { replace: true });
    else if (user) {
      arrivedSignedIn.current = true;
      navigate("/admin", { replace: true });
    }
  }, [loading, user, navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const account = await login(email.trim(), password);
      arrivedSignedIn.current = true;
      // Hard split: administrator / staff accounts are sent straight to the
      // console instead of the owner dashboard.
      if (account.role === "admin" || account.role === "staff") {
        navigate("/admin", { replace: true });
        return;
      }
      navigate(from.startsWith("/admin") ? "/dashboard" : from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to sign in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

  return (
    <>
      <Seo title="Owner Login" description="Sign in to your Mysore Yellow Pages owner dashboard to manage listings, orders and reviews." path="/login" />
      <div className="flex min-h-screen">
        <div className="hidden lg:flex lg:w-1/2 relative">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20illustration%20of%20a%20vibrant%20South%20Indian%20city%20street%20in%20Mysore%20with%20the%20palace%20silhouette%2C%20market%20shops%20and%20auto%20rickshaws%2C%20golden%20hour%20light%2C%20deep%20amber%20and%20olive%20tones%2C%20rich%20textured%20editorial%20style%20with%20strong%20dark%20contrast&width=1200&height=1400&seq=myyp-login-bg&orientation=portrait"
            alt="Mysore city"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/30"></div>
          <div className="relative z-10 flex flex-col justify-between p-10 text-background-50">
            <Link to="/" className="flex items-center" aria-label={settings.siteName}>
              <BrandLogo className="h-14" tone="dark" />
            </Link>
            <div>
              <h2 className="font-heading text-3xl font-extrabold leading-tight">
                Manage your business listing in one place
              </h2>
              <p className="mt-3 max-w-md text-sm text-background-50/80 leading-relaxed">
                Update your profile, track views and leads, respond to reviews and manage your plan —
                all from your owner dashboard.
              </p>
            </div>
          </div>
        </div>

        <div className="flex w-full lg:w-1/2 items-center justify-center bg-background-50 px-4 py-12">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-8 flex items-center lg:hidden" aria-label={settings.siteName}>
              <BrandLogo className="h-12" />
            </Link>

            <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Member sign in</h1>
            <p className="mt-2 text-sm text-foreground-600">
              New here?{" "}
              <Link to="/register" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                Create an account
              </Link>
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              On the team?{" "}
              <Link to="/admin" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                Sign in to the admin console
              </Link>
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  className={inputClass}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`${inputClass} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 cursor-pointer"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {submitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-login-box-line"></i>}
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>

            {IS_MOCK && (
              <div className="mt-6 rounded-md border border-dashed border-background-300 bg-background-100/70 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold text-foreground-700">
                  <i className="ri-information-line text-primary-600"></i> Demo account (preview mode)
                </p>
                <p className="mt-2 text-xs text-foreground-600">
                  Email: <span className="font-mono text-foreground-900">{DEMO_ACCOUNT.email}</span>
                  <br />
                  Password: <span className="font-mono text-foreground-900">{DEMO_ACCOUNT.password}</span>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setEmail(DEMO_ACCOUNT.email);
                    setPassword(DEMO_ACCOUNT.password);
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                >
                  <i className="ri-magic-line"></i> Fill demo credentials
                </button>
              </div>
            )}

            <p className="mt-6 text-center text-xs text-foreground-500">
              <Link to="/" className="hover:text-foreground-800 cursor-pointer">← Back to {settings.siteName}</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}